import type * as T from "../types";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type BillPageWire = Schemas["BillPage"];
export type BillSummaryWire = Schemas["BillSummary"];
export type BillDetailWire = Schemas["BillDetailResponse"];
export type BillFiguresWire = Schemas["BillFigures"];
export type DebitNotePageWire = Schemas["DebitNotePage"];
export type DebitNoteSummaryWire = Schemas["DebitNoteSummary"];
export type DebitNoteDetailWire = Schemas["DebitNoteDetailResponse"];
export type PaymentPageWire = Schemas["PaymentPage"];
export type PaymentSummaryWire = Schemas["PaymentSummary"];
export type PaymentDetailWire = Schemas["PaymentDetailResponse"];

function money(value: unknown): T.Money {
  const [whole = "0", fraction = ""] = String(value).split(".");
  return `${whole}.${fraction.padEnd(2, "0").slice(0, 2)}`;
}

function paisa(value: unknown): number {
  return Math.round(Number(value) * 100);
}

export function toBillSummary(wire: BillSummaryWire): T.BillSummary {
  return {
    id: wire.id,
    number: wire.number,
    supplierId: wire.supplierId,
    supplierName: wire.supplierName,
    date: wire.billedOn,
    due: wire.dueOn,
    vendorInvoiceNo: wire.supplierReference,
    status: wire.approved ? "approved" : "draft",
    total: money(wire.total),
    balance: money(wire.owed),
    taxable: money(wire.taxable),
  };
}

export function toBill(wire: BillDetailWire): T.Bill {
  const { bill } = wire;
  const tax = money(bill.taxAmount);
  return {
    ...toBillSummary(bill.summary),
    fromPurchaseOrder: wire.fromPurchaseOrder ?? undefined,
    ...(bill.paymentTermsDays == null ? {} : { paymentTermsDays: String(bill.paymentTermsDays) }),
    lines: wire.lines.map((line) => ({
      n: Number(line.lineNumber),
      description: line.description,
      accountId: line.accountId,
      accountName: line.accountName,
      // The API keeps a line's amount, not how it was reached.
      quantity: "1",
      unitPrice: money(line.amount),
      taxCodeId: "",
      taxLabel: bill.taxRecoverable ? "Recoverable" : "Blocked",
      capital: line.capital,
      lineNet: money(line.amount),
    })),
    subtotal: money(bill.subtotal),
    recoverableVat: bill.taxRecoverable ? tax : "0.00",
    nonRecoverableTax: bill.taxRecoverable ? "0.00" : tax,
    attachments: [],
    history: {
      createdBy: "",
      createdAt: bill.createdAt,
      ...(bill.approvedAt === null ? {} : { approvedAt: bill.approvedAt }),
    },
  };
}

export function toBillQuery(q: T.ListQuery): Record<string, string | undefined> {
  return {
    minAmount: q.minAmount,
    q: q.q,
    from: q.from,
    to: q.to,
    approved: q.status === "draft" ? "false" : q.status === "approved" ? "true" : undefined,
    pageSize: q.pageSize === undefined ? undefined : String(q.pageSize),
    cursor: q.cursor,
  };
}

export function toDebitNoteSummary(wire: DebitNoteSummaryWire): T.DebitNoteSummary {
  return {
    id: wire.id,
    number: wire.number ?? null,
    supplierId: wire.supplierId,
    supplierName: wire.supplierName,
    date: wire.date,
    againstBillId: wire.againstBillId ?? null,
    againstNumber: wire.againstNumber ?? null,
    reason: wire.reason ?? null,
    status: wire.approved ? "approved" : "draft",
    total: money(wire.total),
    available: money(wire.available),
  };
}

export function toDebitNote(wire: DebitNoteDetailWire): T.DebitNote {
  const { note } = wire;
  const tax = money(note.taxAmount);
  return {
    ...toDebitNoteSummary(note.summary),
    reasonDetail: note.reasonDetail ?? null,
    lines: wire.lines.map((line) => ({
      n: Number(line.lineNumber),
      description: line.description,
      accountId: line.accountId,
      accountName: line.accountName,
      quantity: "1",
      unitPrice: money(line.amount),
      taxCodeId: "",
      taxLabel: note.taxRecoverable ? "Recoverable" : "Blocked",
      capital: line.capital,
      lineNet: money(line.amount),
    })),
    subtotal: money(note.subtotal),
    recoverableVat: note.taxRecoverable ? tax : "0.00",
    nonRecoverableTax: note.taxRecoverable ? "0.00" : tax,
    history: {
      createdBy: "",
      createdAt: note.createdAt,
      ...(note.approvedAt === null ? {} : { approvedAt: note.approvedAt }),
    },
  };
}

export function toNewDebitNote(dto: T.NewDebitNote, currency: string) {
  return {
    supplierId: dto.supplierId,
    supplierReference: "",
    billedOn: dto.date,
    dueOn: dto.date,
    currencyCode: currency,
    againstBillId: dto.againstBillId,
    reason: dto.reason,
    reasonDetail: dto.reasonDetail || null,
  };
}

const CREDIT_KIND: Record<Schemas["SupplierCreditKind"], T.SupplierCreditKind> = {
  advance: "advance",
  debitNote: "debitNote",
};

export function toBillCredits(wire: Schemas["BillCredits"]): T.BillCredits {
  return {
    available: wire.available.map((c) => ({
      kind: CREDIT_KIND[c.kind],
      sourceId: c.sourceId,
      number: c.number,
      date: c.date,
      available: money(c.available),
    })),
    used: wire.used.map((c) => ({
      kind: CREDIT_KIND[c.kind],
      sourceId: c.sourceId,
      number: c.number,
      date: c.appliedOn,
      amount: money(c.amount),
    })),
  };
}

type PurchaseOrderSummaryWire = Schemas["PurchaseOrderSummary"];
export type PurchaseOrderPageWire = Schemas["PurchaseOrderPage"];
export type PurchaseOrderDetailWire = Schemas["PurchaseOrderDetailResponse"];

export function toPurchaseOrderSummary(wire: PurchaseOrderSummaryWire): T.PurchaseOrderSummary {
  return {
    id: wire.id,
    number: wire.number ?? null,
    status: wire.status as T.PurchaseOrderStatus,
    supplierId: wire.supplierId,
    supplierName: wire.supplierName,
    date: wire.orderedOn,
    expected: wire.expectedOn,
    reference: wire.reference ?? null,
    total: money(wire.total),
    lineCount: Number(wire.lineCount),
    linesReceived: Number(wire.linesReceived),
  };
}

export function toPurchaseOrder(wire: PurchaseOrderDetailWire): T.PurchaseOrder {
  const { order } = wire;
  return {
    ...toPurchaseOrderSummary(order.summary),
    currency: order.currencyCode,
    supplierTaxId: order.supplierTaxId ?? undefined,
    deliverTo: order.deliverTo ?? null,
    terms: order.terms ?? null,
    note: order.note ?? null,
    subtotal: money(order.subtotal),
    vat: money(order.vatAmount),
    sentAt: order.sentAt ?? null,
    lines: order.lines.map((line) => ({
      id: line.id,
      n: Number(line.lineNumber),
      description: line.description,
      accountId: line.accountId,
      accountName: wire.accountNames[String(line.lineNumber)] ?? "",
      quantity: String(Number(line.quantity)),
      unitPrice: money(line.unitPrice),
      vatRatePct: String(Number(line.vatRate)),
      lineNet: money(line.netAmount),
      received: String(Number(line.received)),
      billed: String(Number(line.billed)),
    })),
    receipts: order.receipts.map((receipt) => ({
      lineId: receipt.lineId,
      description: receipt.description,
      quantity: String(Number(receipt.quantity)),
      date: receipt.receivedOn,
    })),
    bills: order.bills.map((bill) => ({ id: bill.id, number: bill.number ?? null })),
    history: { createdBy: "", createdAt: order.createdAt },
  };
}

export function toPurchaseOrderRequest(dto: T.NewPurchaseOrder, currency: string) {
  return {
    supplierId: dto.supplierId,
    orderedOn: dto.date,
    expectedOn: dto.expected,
    currencyCode: currency,
    reference: dto.reference ?? null,
    deliverTo: dto.deliverTo ?? null,
    terms: dto.terms ?? null,
    note: dto.note ?? null,
    lines: dto.lines.map((line) => ({
      description: line.description,
      accountId: line.accountId,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      vatRate: line.vatRatePct,
      capital: line.capital,
    })),
  };
}

export function toPaymentSummary(wire: PaymentSummaryWire): T.SupplierPaymentSummary {
  return {
    id: wire.id,
    number: wire.number ?? null,
    status: wire.posted ? "posted" : "draft",
    supplierId: wire.supplierId,
    supplierName: wire.supplierName,
    date: wire.paidOn,
    method: wire.method,
    reference: wire.reference ?? null,
    appliedTo: wire.appliedTo,
    gross: money(wire.gross),
    withheld: money(wire.withheld),
    net: money(wire.net),
    advance: money(wire.advance),
  };
}

export function toPayment(wire: PaymentDetailWire): T.SupplierPayment {
  const { payment } = wire;
  return {
    ...toPaymentSummary(payment.summary),
    journalId: payment.journalId ?? undefined,
    currency: payment.currencyCode,
    paidFromName: wire.paidFromName,
    withheldCode: payment.withheldCode ?? null,
    withheldRate: payment.withheldRate === null ? null : String(Number(payment.withheldRate)),
    applications: payment.applications.map((line) => ({
      billId: line.billId,
      billNumber: line.billNumber,
      amount: money(line.amount),
    })),
    history: {
      createdBy: "",
      createdAt: payment.createdAt,
      approvedAt: payment.postedAt ?? undefined,
    },
  };
}

export function toNewPayment(dto: T.NewSupplierPayment) {
  return {
    supplierId: dto.supplierId,
    paidOn: dto.date,
    bankAccountId: dto.bankAccountId,
    method: dto.method,
    reference: dto.reference || null,
    grossAmount: dto.gross,
    withheldCode: dto.withholding?.code ?? null,
    allocations: dto.allocations.map((line) => ({ billId: line.billId, amount: line.amount })),
  };
}

export function toBillFigures(wire: BillFiguresWire) {
  const count = (n: unknown, one: string) => `${Number(n)} ${one}${Number(n) === 1 ? "" : "s"}`;
  return [
    {
      key: "drafts",
      label: "Awaiting approval",
      value: money(wire.drafts),
      sub: count(wire.draftCount, "bill"),
      tone: "default" as const,
    },
    {
      key: "owed",
      label: "Owed to suppliers",
      value: money(wire.owed),
      sub: count(wire.owedCount, "bill"),
      tone: "default" as const,
    },
    {
      key: "overdue",
      label: "Overdue",
      value: money(wire.overdue),
      sub: count(wire.overdueCount, "bill"),
      tone: Number(wire.overdueCount) > 0 ? ("bad" as const) : ("default" as const),
    },
  ];
}

export function toNewBill(dto: T.NewBill, currency: string) {
  return {
    supplierId: dto.supplierId,
    supplierReference: dto.vendorInvoiceNo,
    billedOn: dto.date,
    dueOn: dto.due,
    currencyCode: currency,
    paymentTermsDays: dto.paymentTermsDays ? Number(dto.paymentTermsDays) : null,
  };
}

/**
 * What `PUT /bills/{id}` takes. A bill carries one tax rate, so the lines' shared tax code decides
 * it. Tax is worked in paisa so the amount sent is the one the screen showed.
 */
export function toBillRevision(dto: Pick<T.NewBill, "lines">, taxCodes: readonly T.TaxCode[]) {
  const code = taxCodes.find((t) => t.id === dto.lines[0]?.taxCodeId);
  const rate = code === undefined || code.ratePct === "" ? 0 : Number(code.ratePct);
  const amounts = dto.lines.map((line) =>
    Math.round(Number(line.quantity) * paisa(line.unitPrice)),
  );
  const subtotal = amounts.reduce((sum, amount) => sum + amount, 0);

  return {
    lines: dto.lines.map((line, index) => ({
      lineNumber: index + 1,
      description: line.description,
      accountId: line.accountId,
      amount: ((amounts[index] ?? 0) / 100).toFixed(2),
      capital: line.capital,
    })),
    taxRatePercent: String(rate),
    taxAmount: (Math.round((subtotal * rate) / 100) / 100).toFixed(2),
    taxRecoverable: code?.recoverable ?? true,
  };
}
